import { RefObject, useEffect } from 'react';
import { useFirstMountState } from 'react-use';
import { TabsProps } from 'src/components/primitives/Tabs/types';
import { Z_INDEX } from 'src/constants/zIndex';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { cj, cm } from 'src/utils/classNames';

interface Props<T extends string, S extends string> extends Required<
	Pick<TabsProps<T, S>, 'variant' | 'indicatorTrack' | 'size'>
> {
	activeIndex: number;
	ref: RefObject<HTMLDivElement | null>;
	indicatorClassName?: string;
}

// Even though the tabs have `z-1`, `-z-1` here is still required by Safari for some reason -_-.
const STYLE = {
	zIndex: Z_INDEX.tab * -1,
};

export default function TabIndicator<T extends string, S extends string>({
	activeIndex,
	variant,
	indicatorTrack,
	size,
	ref,
	indicatorClassName,
}: Props<T, S>) {
	const isFirstMount = useFirstMountState();
	const isMobileScreenSize = useIsMobileScreenSize();

	useEffect(() => {
		const tabEl = ref.current;

		if (!tabEl) {
			return;
		}

		const update = () => {
			requestAnimationFrame(() => {
				const activeChild = tabEl.children[activeIndex];

				if (!(activeChild instanceof HTMLElement)) {
					return;
				}

				const { offsetLeft, clientWidth, clientHeight } = activeChild;

				tabEl.style.setProperty('--active-offset', `${offsetLeft}px`);
				tabEl.style.setProperty('--active-height', `${clientHeight}px`);
				tabEl.style.setProperty('--active-width', `${clientWidth}px`);
			});
		};

		const resizeObserver = new ResizeObserver(update);

		for (const child of tabEl.children) {
			if (child instanceof HTMLElement) {
				resizeObserver.observe(child);
			}
		}

		return () => {
			resizeObserver.disconnect();
		};
	}, [
		activeIndex,
		// These aren't used within the effect but we want to re-run if either has
		// changed. Otherwise, it's possible that the indicator will be misaligned
		// after change. `size` flips at the `xl` breakpoint (e.g. md -> sm), which
		// changes the tab padding without re-running the measurement.
		isMobileScreenSize,
		size,
	]);

	const variantClassName = {
		primary: 'bg-button-primary-bg',
		secondary: 'rounded-sm bg-button-secondary-bg',
	}[variant];

	const sizeClassName = {
		sm: 'h-0.5',
		md: 'h-0.5',
		lg: 'h-[3px]',
	}[size];

	return (
		<>
			{/* Track the primary indicator sits in if specified. Note, should remain */}
			{/* before the indicator in the DOM, otherwise it will cover the indicator. */}
			{indicatorTrack && (
				<span
					className={cj(
						'absolute right-0 bottom-0 left-0 bg-stroke-divider',
						sizeClassName
					)}
					style={STYLE}
				/>
			)}
			<span
				className={cm(
					'absolute w-full',
					'max-w-(--active-width) translate-x-(--active-offset)',
					// Avoids animating the initial tab getting set when it's not the first tab.
					!isFirstMount && 'duration-200 ease-out-smooth',
					// The secondary (pill) indicator must stay vertically centered on the
					// active tab. A taller sibling (e.g. the "Others" dropdown) can stretch
					// the tab row, so bottom-anchoring drops the pill below the centered
					// tab labels.
					variant === 'secondary'
						? 'top-1/2 h-(--active-height) -translate-y-1/2'
						: cj('bottom-0', sizeClassName),
					variantClassName,
					indicatorClassName
				)}
				style={STYLE}
			/>
		</>
	);
}
